import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { z } from "zod"
import type { Category, OrgFromCms, Scenario } from "@simulead/api/cms"
import { demoOrgs } from "./variables"
import type { Table } from "@tanstack/react-table"
import * as Papa from "papaparse"
import { DateTime } from "luxon"
import type { ClientOutput } from "./ts-rest"
import type { getUsers } from "@simulead/api/user"
import type { Call, User } from "@simulead/database/browser"
import {
  countWordsSegmented,
  defaultLanguage,
  getLanguageConfig,
  languages,
  type LanguageCode,
} from "@simulead/shared"
import { getGender } from "gender-detection-from-name"
import translationData from "./frontend-translation-values.json"
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Frontend-only feature flag for journeys visibility.
export function isJourneysFeatureEnabled(_orgName?: string | null): boolean {
  return true
}

// Template journeys that admins can create with one click
export const JOURNEY_TEMPLATES: {
  name: string
  description: string
  scenarioIds: string[]
}[] = [
  {
    name: "Manager Essentials",
    description:
      "Common conversations covering the core skills every people manager needs — coaching, giving feedback, supporting wellbeing, delivering difficult news, and resolving conflict.",
    scenarioIds: ["6", "22", "20", "2"],
  },
]

// Returns templates whose scenarioIds are all present in the org's CMS scenarios
export function getAvailableJourneyTemplates(
  allScenarioIds: string[],
): typeof JOURNEY_TEMPLATES {
  const idSet = new Set(allScenarioIds)
  return JOURNEY_TEMPLATES.filter((t) =>
    t.scenarioIds.every((id) => idSet.has(id)),
  )
}

export const zodTransformToStringOrNull = z.any().transform((item: any) => {
  if (item === "" || item === undefined || item === null) {
    return null
  } else {
    return String(item)
  }
})

export const zodTransformToNumber = z.any().transform((item: any) => {
  return Number(item)
})

export const zodTransformToNumberOrNull = z.any().transform((item: any) => {
  if (item === "" || item === undefined || item === null) {
    return null
  } else {
    return Number(item)
  }
})

// Common personal (non-work) email domains
const personalEmailDomains = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "protonmail.com",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "gmx.com",
  "live.com",
  "msn.com",
]

// Whether an address is on a personal domain. Used to stamp leads for triage
// where personal addresses are accepted (the post-call demo form), rather than
// to reject them.
export function isPersonalEmailAddress(email: string) {
  const domain = email.trim().toLowerCase().split("@")[1]
  return Boolean(domain && personalEmailDomains.includes(domain))
}

// Any well-formed address. The post-call demo form uses this instead of
// workEmailSchema: by then the visitor has already done the call, so the form
// is the only way to keep that lead, and a personal address is better than
// none (leads carry isPersonalEmail for triage instead).
export const demoEmailSchema = z.string().email()

// Reusable work email validation schema
export const workEmailSchema = z
  .string()
  .email()
  .refine(
    (email) => {
      // Check for obviously fake emails
      const fakePatterns = [
        /test@/i,
        /example@/i,
        /fake@/i,
        /temp@/i,
        /dummy@/i,
        /123@/i,
        /abc@/i,
      ]

      // Check if domain is in personal domains list
      if (isPersonalEmailAddress(email)) {
        return false
      }

      // Check if email matches any fake pattern
      for (const pattern of fakePatterns) {
        if (pattern.test(email)) {
          return false
        }
      }

      return true
    },
    {
      message: "Please use a valid work email address",
    },
  )

export const demoEmailGateFormSchema = z.object({
  email: workEmailSchema,
})

// Type for the form schema
export type DemoEmailGateFormValues = z.infer<typeof demoEmailGateFormSchema>

export const focusedDemoFormSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  email: workEmailSchema,
  roleTitle: z.string().min(1, "Role title is required"),
  company: z.string().min(1, "Company is required"),
})

export type FocusedDemoFormValues = z.infer<typeof focusedDemoFormSchema>

export function getMinutesAndSecondsFromSeconds(seconds: number): {
  minutes: number
  seconds: number
} {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.round(seconds % 60)
  return {
    minutes,
    seconds: remainingSeconds,
  }
}

export function getHoursMinutesAndSecondsFromSeconds(seconds: number): {
  hours: number
  minutes: number
  seconds: number
} {
  const hours = Math.floor(seconds / 3600)
  const remainingMinutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60
  return {
    hours,
    minutes: remainingMinutes,
    seconds: remainingSeconds,
  }
}

export function getHoursAndMinutesFromSeconds(seconds: number): {
  hours: number
  minutes: number
} {
  const hours = Math.floor(seconds / 3600)
  const remainingMinutes = Math.round((seconds % 3600) / 60)
  return {
    hours,
    minutes: remainingMinutes,
  }
}

export function getHoursAndMinutesStringFromSeconds(seconds: number): string {
  const { hours, minutes } = getHoursAndMinutesFromSeconds(seconds)
  return `${hours > 0 ? `${hours}h ` : ""}${minutes}m`
}

// Numeric minutes (2dp) so CSV exports can be summed and sorted in spreadsheets
export function getMinutesNumberFromSeconds(seconds: number) {
  return Math.round((seconds / 60) * 100) / 100
}

export function getDurationTextFromSeconds(seconds: number) {
  const { minutes, seconds: remainingSeconds } =
    getMinutesAndSecondsFromSeconds(Math.round(seconds))

  return `${minutes} ${minutes === 1 ? "min" : "mins"} ${remainingSeconds} ${
    remainingSeconds === 1 ? "sec" : "secs"
  }`
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number
    sizeType?: "accurate" | "normal"
  } = {},
) {
  const { decimals = 0, sizeType = "normal" } = opts

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"]
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"]
  if (bytes === 0) return "0 Byte"
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === "accurate"
      ? (accurateSizes[i] ?? "Bytest")
      : (sizes[i] ?? "Bytes")
  }`
}

export function calculatePresentationPace(
  text: string,
  durationSeconds: number,
  languageCode?: string,
): number {
  const words = countWordsSegmented(text, languageCode)
  const minutes = durationSeconds / 60
  return Math.round(words / minutes)
}

export function getDemoOrg(demoOrgId?: string) {
  const demoOrg =
    demoOrgs.find((demoOrg) => demoOrg.id === demoOrgId) ??
    demoOrgs.find((item) => item.id === "default")!

  return { demoOrg }
}

export function exportTableToCsv(
  table: Table<any>,
  excludeHeaderNames: string[] = [],
  options?: {
    // Inject constant columns into every row (e.g., { Module: "Difficult Conversations" })
    extraStaticColumns?: Record<string, string | number | null | undefined>
    // Override the default filename if provided
    fileNameOverride?: string
    // Returns the row's duration in seconds so "Time spent" exports as numeric minutes
    getDurationSeconds?: (original: any) => number
  },
) {
  const filteredRows = table.getPrePaginationRowModel().rows

  // Transform the data to match the CSV format
  const csvData = filteredRows.map((row) => {
    const user = row.original.user
    // Ensure insertion order for headers: extra static columns (e.g., Module) first, then identity, then metrics
    const obj: Record<string, any> = {}

    // Inject static columns first, keeping order stable
    if (options?.extraStaticColumns) {
      for (const [key, value] of Object.entries(options.extraStaticColumns)) {
        obj[key] = value ?? ""
      }
    }

    // Identity columns
    obj["First Name"] = user.firstName
    obj["Last Name"] = user.lastName
    obj["Email"] = user.email

    for (const cell of row.getVisibleCells()) {
      const headerName = cell.column.columnDef.header
      if (
        typeof headerName === "string" &&
        excludeHeaderNames.includes(headerName) === false
      ) {
        if (headerName === "Time spent" && options?.getDurationSeconds) {
          // Export as a number of minutes rather than a "1h 11m" string
          obj["Time spent (minutes)"] = getMinutesNumberFromSeconds(
            options.getDurationSeconds(row.original),
          )
        } else {
          obj[headerName] = cell.getValue()
        }
      }
    }

    return obj
  })

  // Convert to CSV
  const csv = Papa.unparse(csvData)

  // Create a blob and download link
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute(
    "download",
    options?.fileNameOverride ??
      `Export_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm")}.csv`,
  )
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// Canonical mapping between includeStatType and UI module labels
export type IncludeStatType = "call" | "presentation" | "coaching"
export const includeStatTypeToLabel: Record<IncludeStatType, string> = {
  call: "Difficult Conversations",
  presentation: "Presentations",
  coaching: "AI Coaching",
}

// Also expose a mapping from permission names to includeStatType to avoid duplication across files
export const permissionNameToStatType: Record<
  "Conversations" | "Presentations" | "Coaching",
  IncludeStatType
> = {
  Conversations: "call",
  Presentations: "presentation",
  Coaching: "coaching",
}

// Build and download a combined CSV for all enabled modules.
// datasets: map of includeStatType -> users payload from getUsers
export function exportAllModulesUsageCsv(
  {
    datasets,
    moduleOrder = ["call", "presentation", "coaching"],
  }: {
    datasets: Partial<
      Record<IncludeStatType, Awaited<ReturnType<typeof getUsers>>["users"]>
    >
    moduleOrder?: IncludeStatType[]
  },
  options?: { fileNameOverride?: string },
) {
  // Identity first
  const headers: string[] = ["First Name", "Last Name", "Email", "Last Active"]

  // Determine included modules in the desired order
  const includedModules = moduleOrder.filter(
    (m) => datasets[m]?.length !== undefined,
  )

  // For each module, append its metric headers with namespacing
  for (const statType of includedModules) {
    const label = includeStatTypeToLabel[statType]
    if (statType === "call") {
      headers.push(
        `${label} — Unique Scenarios`,
        `${label} — Total Sessions`,
        `${label} — Time Spent (minutes)`,
      )
    }
    if (statType === "presentation") {
      headers.push(
        `${label} — Practice Sessions`,
        `${label} — Q&A Sessions`,
        `${label} — Time Spent (minutes)`,
      )
    }
    if (statType === "coaching") {
      headers.push(`${label} — Coaching Sessions`, `${label} — Time Spent (minutes)`)
    }
  }

  type Row = Record<string, string | number | "">
  type RowContainer = { row: Row; _activity: Record<IncludeStatType, boolean> }
  const byEmail = new Map<string, RowContainer>()

  function ensureRow(user: {
    firstName: string | null
    lastName: string | null
    email: string
    lastActive: Date | null
  }): RowContainer {
    const existing = byEmail.get(user.email)
    if (existing) return existing
    const baseRow: Row = {
      "First Name": user.firstName ?? "",
      "Last Name": user.lastName ?? "",
      Email: user.email,
      "Last Active": user.lastActive
        ? DateTime.fromISO(user.lastActive as unknown as string).toLocaleString(
            DateTime.DATETIME_SHORT,
          )
        : "Unknown",
    }
    // Initialize all module headers as blank strings
    for (const statType of includedModules) {
      const label = includeStatTypeToLabel[statType]
      if (statType === "call") {
        baseRow[`${label} — Unique Scenarios`] = ""
        baseRow[`${label} — Total Sessions`] = ""
        baseRow[`${label} — Time Spent (minutes)`] = ""
      }
      if (statType === "presentation") {
        baseRow[`${label} — Practice Sessions`] = ""
        baseRow[`${label} — Q&A Sessions`] = ""
        baseRow[`${label} — Time Spent (minutes)`] = ""
      }
      if (statType === "coaching") {
        baseRow[`${label} — Coaching Sessions`] = ""
        baseRow[`${label} — Time Spent (minutes)`] = ""
      }
    }
    const container: RowContainer = {
      row: baseRow,
      _activity: { call: false, presentation: false, coaching: false },
    }
    byEmail.set(user.email, container)
    return container
  }

  // Populate rows per module
  const callUsers = datasets.call ?? []
  for (const item of callUsers) {
    const container = ensureRow(item.user)
    const stats = item.callStats
    const hasActivity = (stats?.totalCalls ?? 0) > 0
    container._activity.call = hasActivity
    if (hasActivity) {
      const label = includeStatTypeToLabel.call
      container.row[`${label} — Unique Scenarios`] =
        stats?.completedScenarios ?? 0
      container.row[`${label} — Total Sessions`] = stats?.totalCalls ?? 0
      container.row[`${label} — Time Spent (minutes)`] =
        getMinutesNumberFromSeconds(stats?.totalDurationSeconds ?? 0)
    }
  }

  const presentationUsers = datasets.presentation ?? []
  for (const item of presentationUsers) {
    const container = ensureRow(item.user)
    const stats = item.presentationStats
    const hasActivity = (stats?.totalSessions ?? 0) > 0
    container._activity.presentation = hasActivity
    if (hasActivity) {
      const label = includeStatTypeToLabel.presentation
      container.row[`${label} — Practice Sessions`] = stats?.totalSessions ?? 0
      container.row[`${label} — Q&A Sessions`] = stats?.qandaSessions ?? 0
      container.row[`${label} — Time Spent (minutes)`] =
        getMinutesNumberFromSeconds(stats?.totalDurationSeconds ?? 0)
    }
  }

  const coachingUsers = datasets.coaching ?? []
  for (const item of coachingUsers) {
    const container = ensureRow(item.user)
    const stats = item.coachingStats
    const hasActivity = (stats?.totalCalls ?? 0) > 0
    container._activity.coaching = hasActivity
    if (hasActivity) {
      const label = includeStatTypeToLabel.coaching
      container.row[`${label} — Coaching Sessions`] = stats?.totalCalls ?? 0
      container.row[`${label} — Time Spent (minutes)`] =
        getMinutesNumberFromSeconds(stats?.totalDurationSeconds ?? 0)
    }
  }

  // Finalize rows in header order
  const rows: Row[] = []
  for (const container of byEmail.values()) {
    const out: Row = {}
    for (const h of headers) {
      out[h] = (container.row[h] as any) ?? ""
    }
    rows.push(out)
  }

  const csv = Papa.unparse({ fields: headers, data: rows as any })
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute(
    "download",
    options?.fileNameOverride ??
      `Usage_All-Modules_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm")}.csv`,
  )
  link.style.visibility = "hidden"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export function formatRangeForFilename({ from, to }: { from: Date; to: Date }) {
  const fromStr = DateTime.fromJSDate(from).toFormat("yyyy-MM-dd")
  const toStr = DateTime.fromJSDate(to).toFormat("yyyy-MM-dd")
  return `${fromStr}_to_${toStr}`
}

// Re-export from shared for backward compatibility and centralized logic
export {
  getObjectivesAchievedOutcomeFromMarkdown,
  type ObjectivesAchievedOutcome,
} from "@simulead/shared"
import {
  getIsCallCompletedFromFeedback,
  getCallProvider,
  getVapiLanguageOverride,
} from "@simulead/shared"

// Determines if a call was completed successfully (objectives achieved: Yes)
// Used to set the banner variant (green "achieved" vs amber "not_or_partial").
// Reads the newest analysis's structured objectives status first, falling
// back to parsing the (multi-language) markdown for calls that predate it.
// A call list row carries `completed` (worked out server-side); a single call
// (the call page) carries its feedback, so it is read from that instead.
export function getIsCallCompleted(
  call:
    | ClientOutput<"getCallsForUser">["calls"][number]["call"]
    | Parameters<typeof getIsCallCompletedFromFeedback>[0],
): boolean {
  return "completed" in call
    ? call.completed
    : getIsCallCompletedFromFeedback(call)
}

export const workosLocalStorageKey = "workosProfileSessionToken"
export const testingLocalStorageKey = "testingToken"
export const loginTokenLocalStorageKey = "loginToken"
export const scenarioCreatorCallTokenLocalStorageKey =
  "scenarioCreatorCallToken"
export const referralCodeLocalStorageKey = "referralCode"
// Vapi assistant ID for character roleplay ("SimuLead Assistant"). This is the correct base for
// any flow that injects a character system prompt via assistantOverrides (demo, media interview,
// onboarding, and the main product scenario practice).
export const simuleadAssistantId = "0c40d884-521e-4851-a08d-86166eb9463b"

/**
 *
 * Filters out scenarios that Ambr staff have access to but that we don't want cluttering the dashboard experience (others' created custom scenarios). Only includes scenarios that you can navigate to in the dashboard through the categories cards.
 */
export function getNavigatableScenariosFromScenarios({
  scenarios: allScenarios,
  user,
  categories,
  filterByCategory,
}: {
  scenarios: Scenario[]
  categories: Category[]
  user: User
  filterByCategory?: Category
}) {
  let scenarios: Scenario[] = []

  if (filterByCategory) {
    for (const { scenarios_id } of filterByCategory.scenarios) {
      const scenario = allScenarios.find((s) => s.id === scenarios_id)
      if (scenario) scenarios.push(scenario)
    }
  } else {
    // Allow scenarios where we have access to the category
    scenarios = allScenarios.filter((scenario) => {
      let showScenario = false
      for (const {
        categories_id: { id: categoryId },
      } of scenario.categories) {
        const category = categories.find((c) => c.id === categoryId)
        if (category) showScenario = true
      }
      return showScenario
    })
  }

  scenarios = scenarios.filter((scenario) => {
    if (scenario.createdForUserId && user.id !== scenario.createdForUserId) {
      return false
    }
    return true
  })

  return { scenarios }
}

export function getCallDurationSeconds(call: Call) {
  const { startedAt, endedAt } = call
  if (!startedAt || !endedAt) return { durationSeconds: undefined }
  const durationSeconds = Math.abs(
    DateTime.fromISO(endedAt as unknown as string).diff(
      DateTime.fromISO(startedAt as unknown as string),
      "seconds",
    ).seconds,
  )
  return { durationSeconds }
}

export let apiBaseUrl = import.meta.env.VITE_API_BASE_URL
// Auto-detect portless API URL from the current origin (e.g. eng-1282.simulead-fe.localhost → eng-1282.simulead-api.localhost)
if (window.location.hostname.endsWith(".simulead-fe.localhost")) {
  apiBaseUrl = window.location.origin.replace("simulead-fe", "simulead-api")
  console.log("Using portless API base URL:", apiBaseUrl)
} else if (import.meta.env.VITE_VERCEL_GIT_PULL_REQUEST_ID) {
  apiBaseUrl = `https://simulead-api-pr-${
    import.meta.env.VITE_VERCEL_GIT_PULL_REQUEST_ID
  }-265295624208.europe-west2.run.app`
  console.log("Using preview API base URL: ", apiBaseUrl)
}

// Geolocation utilities for location-based voice selection
// Reads the country code set by Vercel Edge Middleware from the geo-country cookie

// Cache the country to avoid parsing cookies on every render
let cachedCountry: string | null = null

// Returns the user's country code (e.g. "US") from the geo-country cookie, or
// "UNKNOWN" if not available or when running in a non-DOM environment
export function getUserCountry(): string {
  if (cachedCountry !== null) return cachedCountry

  if (typeof document === "undefined" || !document.cookie) {
    cachedCountry = "UNKNOWN"
    return cachedCountry
  }

  const match = document.cookie.match(/(?:^|; )geo-country=([^;]+)/)
  const rawCountry = match?.[1]
  cachedCountry = rawCountry ? rawCountry.toUpperCase() : "UNKNOWN"
  return cachedCountry
}

// Check if the user is in the United States based on geo-country cookie
export function isUserInUS(): boolean {
  return getUserCountry() === "US"
}

// ── Live API reachability fallback (ENG-1452) ─────────────────────────────
// Google Live API's websocket is blocked by the Great Firewall, and it can
// blackhole (hang, not error) there — so a failed connection marks Live API
// unreachable for 24h and calls downgrade to the tuned Vapi pipeline. The
// mark is cleared the moment a Live API call connects successfully.
const liveApiUnreachableKey = "liveApiUnreachableUntil"

export type LiveApiFailureReason = "unreachable" | "token-error"

export function clearLiveApiUnreachable() {
  localStorage.removeItem(liveApiUnreachableKey)
}

function isLiveApiMarkedUnreachable() {
  return Date.now() < Number(localStorage.getItem(liveApiUnreachableKey) ?? 0)
}

/**
 * Handle a failed Live API connection: returns the provider the call should
 * continue on. Only languages with a tuned Vapi pipeline (vapiLanguageOverrides,
 * i.e. Mandarin) may downgrade to Vapi — falling back on an untuned language
 * would produce a broken-accent call, so those keep Live API (and its existing
 * error modal). Only a websocket-level failure ("unreachable" — the firewall
 * case) sets the 24h sticky mark; a token-API error is our backend's problem
 * and falls back for this call only.
 */
export function getLiveApiFallbackProvider({
  languageCode,
  reason,
}: {
  languageCode: string
  reason: LiveApiFailureReason
}): "Vapi" | "GoogleLiveAPI" {
  if (getVapiLanguageOverride(languageCode)) {
    // Only a language that actually downgrades may set the (global) sticky
    // mark — an untuned language must never poison Mandarin's provider choice
    if (reason === "unreachable") {
      localStorage.setItem(
        liveApiUnreachableKey,
        String(Date.now() + 24 * 60 * 60 * 1000),
      )
    }
    return "Vapi"
  } else {
    return "GoogleLiveAPI"
  }
}

/**
 * The call provider to actually use: the language's preferred provider, except
 * that languages with a tuned Vapi pipeline (vapiLanguageOverrides, i.e.
 * Mandarin) downgrade from Live API to Vapi for detected-mainland-China users
 * (skips the doomed connection attempt) or after a recent failed Live API
 * connection (VPN/proxy users the geo cookie misses).
 */
export function getPreferredCallProvider(
  languageCode: string,
): "Vapi" | "GoogleLiveAPI" {
  const preferred = getCallProvider(languageCode)
  const hasTunedVapiPipeline = Boolean(getVapiLanguageOverride(languageCode))
  if (
    preferred === "GoogleLiveAPI" &&
    hasTunedVapiPipeline &&
    (getUserCountry() === "CN" || isLiveApiMarkedUnreachable())
  ) {
    return "Vapi"
  } else {
    return preferred
  }
}

/**
 * Gets the language label for a BCP-47 code
 */
export function getLanguageLabel(bcp47Code: string): string {
  const labelMap: Record<string, string> = {
    "en-GB": "English",
    "en-US": "English",
    "fr-FR": "French",
    "fr-CA": "French",
    "cmn-Hans-CN": "Mandarin Chinese",
    "cmn-CN": "Mandarin Chinese",
    "de-DE": "German",
    "es-ES": "Spanish",
    "es-US": "Spanish",
    "it-IT": "Italian",
    "ja-JP": "Japanese",
    "ko-KR": "Korean",
    "pt-BR": "Portuguese",
    "pt-PT": "Portuguese",
    "ru-RU": "Russian",
    "ar-XA": "Arabic",
    "hi-IN": "Hindi",
    "nl-NL": "Dutch",
    "pl-PL": "Polish",
    "tr-TR": "Turkish",
    "vi-VN": "Vietnamese",
    "th-TH": "Thai",
    "id-ID": "Indonesian",
    "uk-UA": "Ukrainian",
  }
  return labelMap[bcp47Code] ?? "English"
}

export type FrontendScenarioTranslation = {
  scenarioId: string
  language: LanguageCode
  translations: {
    context: string
    keyDetails: { text: string }[]
    suggestedConversationFlow: { text: string }[]
    objectives: { text: string }[]
  }
}

// The bundled translation JSON predates the string-id migration and still
// stores numeric scenarioIds — coerce so lookups compare "1660" to "1660".
const frontendScenarioTranslations: FrontendScenarioTranslation[] = (
  translationData as unknown as (Omit<
    FrontendScenarioTranslation,
    "scenarioId"
  > & { scenarioId: number | string })[]
).map((t) => ({ ...t, scenarioId: String(t.scenarioId) }))

export function hasFrontendTranslations(scenarioId: string): boolean {
  return frontendScenarioTranslations.some((t) => t.scenarioId === scenarioId)
}

export function getFrontendTranslationLanguages(
  scenarioId: string,
): LanguageCode[] {
  if (!hasFrontendTranslations(scenarioId)) {
    return []
  }

  return frontendScenarioTranslations
    .filter((t) => t.scenarioId === scenarioId)
    .map((t) => t.language)
}

export function getFrontendTranslation(
  scenarioId: string,
  language: LanguageCode,
): FrontendScenarioTranslation | undefined {
  return frontendScenarioTranslations.find(
    (t) => t.scenarioId === scenarioId && t.language === language,
  )
}

// A scenario's on-screen content in one language. A translation generated in
// /staff is a complete copy of the scenario (the API's scenarioTranslations)
// and wins; otherwise the translations bundled with the app are used. Title
// and subtitle only exist on generated copies.
export type ScenarioContentTranslation = {
  title?: string
  subtitle?: string
  context: string
  objectives: { text: string }[]
  keyDetails: { text: string }[]
  suggestedConversationFlow: { text: string }[]
}
type ScenarioTranslationCopy = {
  language: string
  data: ScenarioContentTranslation & {
    difficultyLevels?: {
      levelName: string
      objectives: { text: string }[]
      suggestedConversationFlow: { text: string }[]
    }[]
  }
}

export function getScenarioContentTranslation({
  scenarioId,
  language,
  scenarioTranslations,
  difficultyLevel,
}: {
  scenarioId: string
  language: LanguageCode
  scenarioTranslations: ScenarioTranslationCopy[] | undefined
  // The selected difficulty level: its own objectives / conversation flow
  // replace the base ones, exactly as in the English scenario
  difficultyLevel?: string
}): ScenarioContentTranslation | undefined {
  const copy = scenarioTranslations?.find((item) => item.language === language)
  if (copy) {
    const level = copy.data.difficultyLevels?.find(
      (item) => item.levelName === difficultyLevel,
    )
    return {
      ...copy.data,
      objectives: level?.objectives.length ? level.objectives : copy.data.objectives,
      suggestedConversationFlow: level?.suggestedConversationFlow.length
        ? level.suggestedConversationFlow
        : copy.data.suggestedConversationFlow,
    }
  } else {
    return getFrontendTranslation(scenarioId, language)?.translations
  }
}

// Every language a scenario's on-screen content can be shown in: generated
// copies plus the bundled translations
export function getScenarioContentLanguages({
  scenarioId,
  scenarioTranslations,
}: {
  scenarioId: string
  scenarioTranslations: ScenarioTranslationCopy[] | undefined
}): LanguageCode[] {
  return [
    ...new Set([
      ...(scenarioTranslations ?? []).map((item) => item.language as LanguageCode),
      ...getFrontendTranslationLanguages(scenarioId),
    ]),
  ]
}

// "The pinned device vanished" detector for getUserMedia `{ exact }` failures
// (camera unplugged, Bluetooth headset powered off, iOS Safari rotating
// deviceIds between sessions). Detect by `.name`, NOT
// `instanceof OverconstrainedError`: Safari doesn't reliably expose that
// constructor (it throws a DOMException merely NAMED "OverconstrainedError"),
// so an instanceof check silently misclassifies the vanished device as a hard
// failure there. "NotFoundError" covers engines that report a missing device
// that way instead. Shared by VapiCall / LiveAPICall /
// presentation-interaction (codebase.md #35).
export const isDeviceGoneError = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "name" in error &&
  (error.name === "OverconstrainedError" || error.name === "NotFoundError")

// Onboarding-modal step footers: pin the action row to the dialog's visible
// bottom edge so Continue/Start Practice never scroll out of sight on short
// viewports (the dialog caps at 85vh and macOS hides overlay scrollbars, so
// off-screen buttons read as "cut off"). The negative x-margins re-span the
// dialog's p-6 padding so the pinned background reaches its side edges. No
// negative BOTTOM margin: that would shrink the dialog's natural height and
// make every step overflow by that amount, pinning the footer over the row
// above it even when the content would otherwise fit.
export const onboardingStepFooterClasses =
  "sticky bottom-0 z-10 -mx-6 px-6 pt-4 bg-background"

// A call row's data is complete for feedback purposes once its
// provider-specific payload has landed: Vapi rows also need the end-of-call
// webhook payload (vapiCall) alongside the analysis; Live API rows never get
// one — their analysis is client-triggered at call end. Any future provider
// defaults to "complete" so analysis polling can't hang on it.
export function isCallAnalysisPayloadComplete(call: {
  provider: string
  vapiCall: unknown
}) {
  return call.provider === "Vapi" ? Boolean(call.vapiCall) : true
}

// Effective character gender for gender-keyed default voices (e.g. MiniMax for
// unmapped Mandarin scenarios): the CMS character field wins; name-based
// detection is the fallback for characters without one.
export function getEffectiveCharacterGender({
  cmsGender,
  characterName,
}: {
  cmsGender: string | null | undefined
  characterName: string
}) {
  const normalized = cmsGender?.toLowerCase()
  if (normalized === "male" || normalized === "female") {
    return normalized
  }
  return getGender(characterName)
}

// Display label for a language picker option. Falls back to the raw code —
// NOT getLanguageConfig, whose fallback is the English config's label.
export function languageLabelFor(code: LanguageCode) {
  return languages.find((lang) => lang.code === code)?.label ?? code
}

// The per-user onboarding scenario resolved by the ENG-1521 selection
// ladder, with the global CMS variable as the fallback while the resolution
// hydrates. Shared by the onboarding modal and the post-call banner so the
// fallback rule can't drift between the two.
// Scenario ids are opaque strings (ENG-1138); both sources are null when no
// onboarding scenario is configured anywhere.
export function getResolvedOnboardingScenarioId(cmsBody?: {
  onboardingScenarioResolution?: { scenarioId: string | null }
  variables?: { onboardingScenario: string | null }
}) {
  return (
    cmsBody?.onboardingScenarioResolution?.scenarioId ??
    cmsBody?.variables?.onboardingScenario
  )
}

// Localise a scenario's system prompt for a call — shared by the pre-call
// screen (ScenarioInteraction) and the onboarding modal.
// Non-default language: swap in the matching DB translation row plus
// language-nuance and gender-grammar additions; with no translation row, keep
// the (English) prompt and append a "respond in <language>" instruction so TTS
// receives text in the right language. `appliedDifficultyLevel` is the level
// whose prompt is actually in use ("" = base prompt — always the case for
// onboarding calls, which have no difficulty selection).
export function buildLocalizedScenarioPrompt({
  basePrompt,
  languageCode,
  translations,
  appliedDifficultyLevel,
  characterName,
  userFirstName,
}: {
  basePrompt: string
  languageCode: LanguageCode
  translations:
    | { language: string; difficultyLevel: string; content: string }[]
    | undefined
  appliedDifficultyLevel: string
  characterName: string
  userFirstName: string
}) {
  let scenarioPromptToUse = basePrompt
  if (languageCode !== defaultLanguage.code) {
    // Match the translation to both the language and the difficulty level whose
    // prompt is actually in use (appliedDifficultyLevel; "" = base prompt). If
    // that level has no translation yet, this finds nothing and we fall through
    // to the else branch below, which keeps the (English) prompt and appends a
    // "respond in <language>" instruction — preserving the difficulty content
    // rather than reverting to the base translation.
    // Empty content is a placeholder for a translation still being (or that
    // failed to be) generated — treat it as no translation at all.
    const translation = translations?.find(
      (t) =>
        t.language === languageCode &&
        t.difficultyLevel === appliedDifficultyLevel &&
        t.content.trim() !== ""
    )
    if (translation) {
      scenarioPromptToUse = translation.content

      // Get language config for nuances and build additional prompt instructions
      const languageConfig = getLanguageConfig(languageCode)
      // Extract locale code for gender detection (e.g., "fr" from "fr-FR")
      const languageLocale = languageCode.split("-")[0] as
        | "fr"
        | "en"
        | "de"
        | "es"
        | "it"
        | undefined
      // Trim: CMS character names can carry stray whitespace (e.g. " Erika"),
      // which would make gender detection silently return "unknown"
      const characterGender = getGender(characterName.trim(), languageLocale)
      const userGender = getGender(userFirstName.trim(), languageLocale)

      // Build combined language-specific additions
      const additions: string[] = []

      // Add language nuances if available
      const nuances = (languageConfig as { nuances?: string }).nuances
      if (nuances) {
        additions.push(nuances)
      }

      // Add gender grammar instructions if we have either gender
      if (characterGender !== "unknown" || userGender !== "unknown") {
        let genderInstruction =
          "Please use the correct grammar for this language if it is affected by your gender or the gender of the person you are talking to."
        if (characterGender !== "unknown") {
          genderInstruction += ` You are ${characterGender}.`
        }
        if (userGender !== "unknown") {
          genderInstruction += ` You are speaking to a ${userGender}.`
        }
        additions.push(genderInstruction)
      }

      // Append all additions as one block
      if (additions.length > 0) {
        scenarioPromptToUse += `\n\n${additions.join(" ")}`
      }
    } else {
      // No DB translation available — append a language instruction to the English prompt
      // so the LLM responds in the target language (e.g. Mandarin) instead of English.
      // This ensures ElevenLabs TTS receives text in the correct language for proper pronunciation.
      const languageConfig = getLanguageConfig(languageCode)
      scenarioPromptToUse += `\n\nIMPORTANT: You MUST respond entirely in ${languageConfig.label}. Do not use English at all. Stay fully in character and conduct the entire conversation in ${languageConfig.label}.`
    }
  }
  return scenarioPromptToUse
}

// Retention markers (`*DeletedByRetentionAt`) arrive as ISO strings over
// ts-rest but are typed as Date by Prisma — accept both so callers never cast.
export function formatRetentionDate(deletedAt: string | Date) {
  return (
    typeof deletedAt === "string"
      ? DateTime.fromISO(deletedAt)
      : DateTime.fromJSDate(deletedAt)
  ).toLocaleString(DateTime.DATE_MED)
}
